import axios from "axios";

const API_BASE_URL = "/api/pdfs";

// Configure axios for large file uploads (50MB)
axios.defaults.maxContentLength = 50 * 1024 * 1024; // 50MB
axios.defaults.maxBodyLength = 50 * 1024 * 1024; // 50MB

/**
 * PDF API Service
 * Handles all HTTP requests related to PDF operations
 */
class PDFService {
  /**
   * Create a new PDF or save an image (contact file)
   * - PDF: pdfFile (FormData) or pdfData (base64)
   * - Image: fileType: 'image', fileUrl, fileName, contactId, fileSize (JSON) – image already uploaded
   */
  async createPDF(pdfData) {
    try {
      if (pdfData.fileType === "image" && pdfData.fileUrl && pdfData.fileName) {
        const response = await axios.post(
          API_BASE_URL,
          {
            contactId: pdfData.contactId || null,
            billId: pdfData.billId || null,
            fileType: "image",
            fileUrl: pdfData.fileUrl,
            fileName: pdfData.fileName,
            fileSize: pdfData.fileSize || 0,
            imageCount: 1,
            description: pdfData.description || "",
          },
          { headers: { "Content-Type": "application/json" }, withCredentials: true }
        );
        return response.data;
      }
      if (pdfData.pdfFile instanceof File) {
        const formData = new FormData();
        formData.append("pdfFile", pdfData.pdfFile);
        formData.append("contactId", pdfData.contactId);
        formData.append("fileName", pdfData.fileName || pdfData.pdfFile.name);
        formData.append("fileSize", pdfData.fileSize || pdfData.pdfFile.size);
        formData.append("imageCount", pdfData.imageCount || 1);
        formData.append("description", pdfData.description || "");
        const response = await axios.post(API_BASE_URL, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        return response.data;
      } else if (pdfData.pdfData) {
        const response = await axios.post(API_BASE_URL, pdfData, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
        return response.data;
      } else {
        throw new Error("PDF file or (for image) fileUrl + fileName is required");
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all PDFs for a contact (metadata only)
   */
  async getPDFsByContact(contactId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/contact/${contactId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get single PDF by ID (with full data)
   */
  async getPDFById(pdfId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/${pdfId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get PDF file as blob (for viewing; same as bills - 404 returns handled in UI)
   */
  async getPDFBlob(pdfId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/${pdfId}/file`, {
        responseType: "blob",
        withCredentials: true,
        headers: { Accept: "application/pdf" },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update PDF
   */
  async updatePDF(pdfId, updateData) {
    try {
      const response = await axios.put(`${API_BASE_URL}/${pdfId}`, updateData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete PDF
   */
  async deletePDF(pdfId) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/${pdfId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get PDF count for a contact
   */
  async getPDFCount(contactId) {
    try {
      const response = await axios.get(`${API_BASE_URL}/count/${contactId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all PDFs (for admin or all user's accessible PDFs)
   */
  async getAllPDFs() {
    try {
      const response = await axios.get(`${API_BASE_URL}/all`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error
      return {
        message: error.response.data?.message || "An error occurred",
        status: error.response.status,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        message: "No response from server. Please check your connection.",
        status: 0,
      };
    } else {
      // Error in request setup
      return {
        message: error.message || "An unexpected error occurred",
        status: 0,
      };
    }
  }
}

const pdfServiceInstance = new PDFService();
export default pdfServiceInstance;

